import type { AgentComponentListItem } from '@reach/shared-types/agent-components';
import { useState } from 'react';
import { ConfirmDialog } from '../../../components/common/ConfirmDialog';
import { EditIconLink } from '../../../components/ui/EditIconLink';
import { TrashIconButton } from '../../../components/ui/TrashIconButton';
import { componentPath } from '../agent-component-paths';
import { COMPONENT_TYPE_COPY } from '../agents-copy';
import { COMPONENT_PAGE_COPY } from '../component-page/component-page-copy';
import { shortComponentName } from '../component-page/short-component-name';
import { useDeleteComponent } from '../component-page/useComponentMutations';

interface ComponentRowActionsProps {
  component: Pick<AgentComponentListItem, 'id' | 'type' | 'name' | 'templateKinds'>;
}

export const ComponentRowActions = ({ component }: ComponentRowActionsProps) => {
  const { id, type, name, templateKinds } = component;
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const deleteComponent = useDeleteComponent();
  const shortName = shortComponentName(name);
  const editLabel = `Edit ${name}`;
  const deleteLabel = `Delete ${name}`;
  const componentIsOnTemplates = templateKinds.length > 0;
  const deleteBody = componentIsOnTemplates
    ? COMPONENT_PAGE_COPY.deleteBlockedBody(shortName)
    : COMPONENT_TYPE_COPY[type].deleteBody(shortName);

  const confirmDelete = () => {
    setConfirmingDelete(false);
    deleteComponent.mutate(id);
  };

  return (
    <>
      <EditIconLink to={componentPath(type, id)} label={editLabel} />
      <TrashIconButton
        tooltip="Delete"
        label={deleteLabel}
        disabled={deleteComponent.isPending}
        onClick={() => setConfirmingDelete(true)}
      />
      {confirmingDelete && (
        <ConfirmDialog
          title={COMPONENT_PAGE_COPY.deleteTitle(shortName)}
          confirmLabel={COMPONENT_PAGE_COPY.delete}
          onClose={() => setConfirmingDelete(false)}
          onConfirm={confirmDelete}
          disabled={componentIsOnTemplates}>
          <p>{deleteBody}</p>
        </ConfirmDialog>
      )}
    </>
  );
};
